import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import type { NewConversationCategory } from '@/lib/messages/categories'
import {
    type ConversationFileUpload,
    type ConversationMessage,
    type ConversationSubjectCategory,
    type ConversationSummary,
    type CreateConversationResponse,
    type CreateTwilioTokenResponse,
    type GetResponseExpectationResponse,
    type ListConversationMessagesResponse,
    type ListConversationSubjectsResponse,
    type ListConversationsResponse,
    type SendConversationMessageResponse,
} from '@/lib/messages/types'

const MESSAGES_PAGE_LIMIT = 10

const persistedMessages = (page: ListConversationMessagesResponse | undefined) =>
    Array.isArray(page?.messages) ? page.messages.filter(({ id }) => !id.startsWith('optimistic-')) : []

// Mirrors the backend's ORDERS_MERGED_CATEGORIES (conversation_subjects_controller.rb) — any of
// these categories' subjects can also appear in the merged 'orders' picker cache, so creating a
// conversation for one must invalidate 'subjects-orders' too, not just its own category tag.
const ORDERS_MERGED_CATEGORIES = new Set<NewConversationCategory>(['lab', 'pharmacy_order'])

const responseExpectationTagId = (conversationId: string) => `response-expectation-${conversationId}`
const NEW_CONVERSATION_RESPONSE_EXPECTATION_TAG_ID = 'response-expectation-new'

const fileUploadIdsFromUploads = (fileUploads?: ConversationFileUpload[]) =>
    fileUploads?.map(({ id }) => id).filter(Boolean) ?? []

export const messageCenterApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getConversations: builder.query<ListConversationsResponse, void>({
            query: () => ({
                url: apiEndpoints.messageCenter.conversations,
                params: { page: 1, limit: 100 },
            }),
            providesTags: [{ type: 'MessageCenter', id: 'LIST' }],
        }),
        getConversationsUnreadCount: builder.query<{ unread_count: number }, void>({
            query: () => apiEndpoints.messageCenter.unreadCount,
            providesTags: [{ type: 'MessageCenter', id: 'UNREAD_COUNT' }],
        }),
        getConversation: builder.query<{ conversation: ConversationSummary }, { conversationId: string }>({
            query: ({ conversationId }) => ({ url: apiEndpoints.messageCenter.conversation(conversationId) }),
            providesTags: (_result, _error, { conversationId }) => [{ type: 'MessageCenter', id: conversationId }],
        }),
        // Searches full message history server-side (getConversations only caches each
        // conversation's last message). POST keeps the patient-entered term out of the URL;
        // idempotent retrieval.
        searchConversations: builder.query<ListConversationsResponse, { searchTerm: string }>({
            query: ({ searchTerm }) => ({
                url: apiEndpoints.messageCenter.search,
                method: 'POST',
                body: { search_term: searchTerm },
            }),
            providesTags: [{ type: 'MessageCenter', id: 'LIST' }],
        }),
        getConversationResponseExpectation: builder.query<
            GetResponseExpectationResponse,
            { conversationId: string }
        >({
            query: ({ conversationId }) => ({
                url: apiEndpoints.messageCenter.conversationResponseExpectation(conversationId),
            }),
            providesTags: (_result, _error, { conversationId }) => [
                { type: 'MessageCenter', id: responseExpectationTagId(conversationId) },
            ],
        }),
        getNewConversationResponseExpectation: builder.query<GetResponseExpectationResponse, void>({
            query: () => ({ url: apiEndpoints.messageCenter.newConversationResponseExpectation }),
            providesTags: [{ type: 'MessageCenter', id: NEW_CONVERSATION_RESPONSE_EXPECTATION_TAG_ID }],
        }),
        getConversationSubjects: builder.query<
            ListConversationSubjectsResponse,
            { category: ConversationSubjectCategory }
        >({
            query: ({ category }) => ({
                url: apiEndpoints.messageCenter.conversationSubjects,
                params: { category },
            }),
            providesTags: (_result, _error, { category }) => [{ type: 'MessageCenter', id: `subjects-${category}` }],
        }),
        // Resolves whether a subject (a visit, medication, lab, …) already has a live thread, so a
        // detail-page button can open it directly instead of landing on the inbox. Returns null when
        // the subject has no live conversation yet.
        getConversationForSubject: builder.query<
            { conversation_id: string | null },
            { category: ConversationSubjectCategory; subjectId: string }
        >({
            query: ({ category, subjectId }) => ({
                url: apiEndpoints.messageCenter.conversationForSubject,
                params: { category, subject_id: subjectId },
            }),
            providesTags: (_result, _error, { category, subjectId }) => [
                { type: 'MessageCenter', id: `subject-conversation-${category}-${subjectId}` },
            ],
        }),
        getConversationMessages: builder.infiniteQuery<
            ListConversationMessagesResponse,
            { conversationId: string; aroundMessageId?: string },
            { before_message_id?: string; after_message_id?: string }
        >({
            infiniteQueryOptions: {
                initialPageParam: {},
                getNextPageParam: (oldestPage) => {
                    const messages = persistedMessages(oldestPage)
                    const firstMessage = messages[0]
                    return messages.length < MESSAGES_PAGE_LIMIT || !firstMessage
                        ? undefined
                        : { before_message_id: firstMessage.id }
                },
                getPreviousPageParam: (newestPage) => {
                    if (!newestPage?.has_newer) return undefined
                    const newestMessage = persistedMessages(newestPage).at(-1)
                    return newestMessage ? { after_message_id: newestMessage.id } : undefined
                },
            },
            query: ({ queryArg: { conversationId, aroundMessageId }, pageParam }) => ({
                url: apiEndpoints.messageCenter.conversationMessages(conversationId),
                params: {
                    limit: MESSAGES_PAGE_LIMIT,
                    ...(pageParam.before_message_id ? { before_message_id: pageParam.before_message_id } : {}),
                    ...(pageParam.after_message_id ? { after_message_id: pageParam.after_message_id } : {}),
                    ...(!pageParam.before_message_id && !pageParam.after_message_id && aroundMessageId
                        ? { around_message_id: aroundMessageId }
                        : {}),
                },
            }),
            onQueryStarted: async ({ conversationId }, { dispatch, queryFulfilled }) => {
                const { data } = await queryFulfilled
                if (data.pageParams.length !== 1) return

                dispatch(
                    messageCenterApi.util.updateQueryData('getConversations', undefined, (draft) => {
                        const conversation = draft.conversations?.find(({ id }) => id === conversationId)
                        if (conversation) conversation.unread = false
                    }),
                )
                dispatch(messageCenterApi.util.invalidateTags([{ type: 'MessageCenter', id: 'UNREAD_COUNT' }]))
                // Reading the thread marks its messages read server-side, so its new-message
                // Updates card drops from the feed and the Updates badge on refetch.
                dispatch(messageCenterApi.util.invalidateTags(['Updates', 'UpdatesUnreadCount']))
            },
            providesTags: (_result, _error, { conversationId }) => [
                { type: 'MessageCenter', id: conversationId },
            ],
        }),
        createConversation: builder.mutation<
            CreateConversationResponse,
            {
                message: string
                fileUploads?: ConversationFileUpload[]
                category?: NewConversationCategory
                // The category the subject picker was actually queried under, only used to invalidate that
                // list's cache, since it can differ from the row's own real `category` sent to the backend.
                listingCategory?: ConversationSubjectCategory
                subjectId?: string
            }
        >({
            query: ({ message, fileUploads, category, subjectId }) => {
                const uploadIds = fileUploadIdsFromUploads(fileUploads)
                return {
                    url: apiEndpoints.messageCenter.conversations,
                    method: 'POST',
                    body: {
                        message,
                        ...(uploadIds.length ? { file_upload_ids: uploadIds } : {}),
                        ...(category ? { category } : {}),
                        ...(subjectId ? { subject_id: subjectId } : {}),
                    },
                }
            },
            invalidatesTags: (result, _error, { category, listingCategory, subjectId }) => {
                if (!result) return []
                // Creating a conversation for a subject sets that subject's existing_conversation_id
                // server-side, so the picker's cached list must be invalidated — otherwise re-picking
                // the same subject later wouldn't find its new conversation via existing_conversation_id.
                const invalidatedCategories = new Set([category, listingCategory].filter(Boolean))
                if (category && ORDERS_MERGED_CATEGORIES.has(category)) invalidatedCategories.add('orders')
                return [
                    { type: 'MessageCenter', id: 'LIST' },
                    ...Array.from(invalidatedCategories).map((invalidatedCategory) => ({
                        type: 'MessageCenter' as const, id: `subjects-${invalidatedCategory}`, })),
                    // The per-subject detail-page lookup caches "does this subject have a live thread";
                    // creating one for this subject must refresh it so the button deep-links next time.
                    ...(category && subjectId
                        ? [{ type: 'MessageCenter' as const, id: `subject-conversation-${category}-${subjectId}` }]
                        : []),
                ]
            },
            onQueryStarted: async (_argument, { dispatch, queryFulfilled }) => {
                try {
                    const { data: { conversation, messages } = {} } = await queryFulfilled
                    if (!conversation?.id || !Array.isArray(messages)) return

                    dispatch(
                        messageCenterApi.util.upsertQueryEntries([
                            {
                                endpointName: 'getConversationMessages',
                                arg: { conversationId: conversation.id },
                                value: {
                                    pages: [{ messages }],
                                    pageParams: [{}],
                                },
                            },
                        ]),
                    )
                } catch {
                    return
                }
            },
        }),
        createTwilioToken: builder.mutation<CreateTwilioTokenResponse, void>({
            query: () => ({
                url: apiEndpoints.messageCenter.createToken,
                method: 'POST',
            }),
            extraOptions: { maxRetries: 3 },
            invalidatesTags: [],
        }),
        sendConversationMessage: builder.mutation<
            SendConversationMessageResponse,
            {
                conversationId: string
                message: string
                fileUploads?: ConversationFileUpload[]
            }
        >({
            query: ({ conversationId, message, fileUploads }) => {
                const uploadIds = fileUploadIdsFromUploads(fileUploads)
                return {
                    url: apiEndpoints.messageCenter.conversationMessages(conversationId),
                    method: 'POST',
                    body: { message, ...(uploadIds.length ? { file_upload_ids: uploadIds } : {}) },
                }
            },
            invalidatesTags: (_result, _error, { conversationId }) => [
                { type: 'MessageCenter', id: responseExpectationTagId(conversationId) },
            ],
            onQueryStarted: async (
                { conversationId, message, fileUploads },
                { dispatch, queryFulfilled, requestId },
            ) => {
                const optimisticFileUploads = fileUploads ?? []
                const optimisticMessage: ConversationMessage = {
                    id: `optimistic-${requestId}`,
                    source: 'customer',
                    author_name: '',
                    profile_url: null,
                    message,
                    created_at: new Date().toISOString(),
                    file_uploads: optimisticFileUploads,
                }
                const messagesPatch = dispatch(
                    messageCenterApi.util.updateQueryData('getConversationMessages', { conversationId }, (draft) => {
                        draft.pages[0]?.messages?.push(optimisticMessage)
                    }),
                )
                const conversationsPatch = dispatch(
                    messageCenterApi.util.updateQueryData('getConversations', undefined, (draft) => {
                        const conversation = draft.conversations?.find(({ id }) => id === conversationId)
                        if (conversation) {
                            conversation.last_message = {
                                message: optimisticMessage.message,
                                created_at: optimisticMessage.created_at,
                                profile_url: optimisticMessage.profile_url,
                                ...(optimisticFileUploads.length
                                    ? { file_uploads: optimisticFileUploads }
                                    : {}),
                            }
                        }
                    }),
                )

                try {
                    const { data } = await queryFulfilled
                    const responseMessage = data?.message
                    if (
                        !responseMessage ||
                        typeof responseMessage.id !== 'string' ||
                        typeof responseMessage.message !== 'string' ||
                        typeof responseMessage.created_at !== 'string'
                    ) return
                    dispatch(
                        messageCenterApi.util.updateQueryData(
                            'getConversationMessages',
                            { conversationId },
                            (draft) => {
                                const messages = draft.pages[0]?.messages
                                if (!Array.isArray(messages)) return
                                const sentMessage = messages.find(({ id }) => id === optimisticMessage.id)
                                if (!sentMessage) return
                                sentMessage.id = responseMessage.id
                                sentMessage.message = responseMessage.message
                                sentMessage.created_at = responseMessage.created_at
                                sentMessage.file_uploads = responseMessage.file_uploads ?? sentMessage.file_uploads
                                if (responseMessage.linked_care_items !== undefined) {
                                    sentMessage.linked_care_items = responseMessage.linked_care_items
                                }
                            },
                        ),
                    )
                    dispatch(
                        messageCenterApi.util.updateQueryData('getConversations', undefined, (draft) => {
                            const conversation = draft.conversations?.find(({ id }) => id === conversationId)
                            if (conversation?.last_message?.created_at !== optimisticMessage.created_at) return
                            conversation.last_message.message = responseMessage.message
                            conversation.last_message.created_at = responseMessage.created_at
                            conversation.last_message.file_uploads =
                                responseMessage.file_uploads ?? conversation.last_message.file_uploads
                        }),
                    )
                } catch {
                    messagesPatch.undo()
                    conversationsPatch.undo()
                }
            },
        }),
    }),
    overrideExisting: false,
})

export const {
    useCreateConversationMutation,
    useCreateTwilioTokenMutation,
    useGetConversationsQuery,
    useGetConversationsUnreadCountQuery,
    useGetConversationQuery,
    useLazyGetConversationQuery,
    useGetConversationResponseExpectationQuery,
    useGetNewConversationResponseExpectationQuery,
    useGetConversationSubjectsQuery,
    useLazyGetConversationForSubjectQuery,
    useGetConversationMessagesInfiniteQuery,
    useSearchConversationsQuery,
    useSendConversationMessageMutation,
} = messageCenterApi
